/**
 * Pricing helpers — paymentId encoding + plan lookup against site-profile/v2.
 *
 * The dashboard's /pricing page reads `?paymentId=<base64>` from the URL,
 * forwards it to the pricing-components iframe via postMessage, and the
 * iframe auto-triggers Stripe checkout for the encoded priceId. Same
 * format the pricing-components `usePendingPayment` hook expects.
 *
 * Payload shape (matches the existing GROWTH_PLAN_STRIPE_GENERATED_PAYMENT_ID):
 *   { priceId, creditsPriceId?, timestamp, nonce }
 */

// Pre-generated Growth-plan + credits bundle used by the sidebar
// GrowthPlanPromoCard. Left here for backwards compatibility — new code
// should call encodePaymentId() with a fresh timestamp/nonce instead.
export const GROWTH_PLAN_STRIPE_GENERATED_PAYMENT_ID =
  'eyJwcmljZUlkIjoicHJpY2VfMVRDRDhPQk42S1o4ZEE1QjBmdkNOQ1E3IiwiY3JlZGl0c1ByaWNlSWQiOiJwcmljZV8xVEMwWk9CTjZLWjhkQTVCb1FEeXd1dkkiLCJ0aW1lc3RhbXAiOjE3Nzc2MTg0Nzc4NjMsIm5vbmNlIjoiYnMwaTB5MWJwIn0='

/** Plan name constants — exact match against site-profile/v2's "Plan name" field. */
export const PLAN_NAMES = Object.freeze({
  GROWTH: 'Growth Plan',
  ONE_SITE_LIFETIME: '1 Site Lifetime',
})

/** Hardcoded fallbacks if site-profile/v2 is unreachable. Match prod data. */
export const PRICE_FALLBACKS = Object.freeze({
  GROWTH_MONTHLY: {
    priceId: 'price_1TCD8OBN6KZ8dA5B0fvCNCQ7',
    monthlyUSD: 29,
  },
  ONE_SITE_LIFETIME: {
    priceId: 'price_1TOuBBBN6KZ8dA5BmVqAppbP',
    priceUSD: 79,
    // Marketing anchor shown as struck-through on the promo card. Mirrors
    // the original design's $499 → $149 (~70% off) framing; for $79 we
    // anchor at $199 (~60% off). Pure copy — no Stripe price exists at
    // $199, it's display-only. Change in one place and both repos pick
    // it up once kept in sync.
    anchorUSD: 199,
    // Feature bullets shown inside the orange block of UnlimitedPromoCard
    // (below the "What you get" stat trio). Hardcoded value props rather
    // than the raw site-profile/v2 features — the API list mixes positives
    // with limits ("2 site collaborators") that read as restrictive on a
    // marketing card. We want every line here to be a reason to buy.
    features: [
      'Build with AI Website Builder',
      'Export ready-to-deploy code',
      'AI-powered template editing',
      'Free updates, forever',
    ],
    // Stat trio rendered as a horizontal row below the Checkout button.
    // Same rule: do NOT pull from site-profile/v2's Subtext arrays —
    // those describe per-plan LIMITS ("1 Site", "20 Websites/month",
    // "1 GB storage") which contradict the "Unlimited downloads"
    // marketing on the right column. These are value props instead.
    stats: [
      { headline: 'All Templates', sub: 'Pro library unlocked' },
      { headline: 'Lifetime', sub: 'Pay once, yours forever' },
      { headline: 'Free Hosting', sub: 'SSL on every site' },
    ],
  },
})

/**
 * Encode a Stripe priceId (+ optional creditsPriceId) into the same
 * base64-JSON shape the pricing-components iframe decodes via its
 * PRICING_COMPONENT_PAYMENT_INTENT handler.
 *
 *   { priceId, creditsPriceId?, timestamp, nonce }  →  base64 JSON
 *
 * timestamp + nonce are anti-replay markers (pricing-components rejects
 * very-old timestamps + dedupes by nonce). Generate a fresh one per click.
 */
export function encodePaymentId({ priceId, creditsPriceId } = {}) {
  if (!priceId || typeof priceId !== 'string') {
    throw new Error('encodePaymentId: priceId is required')
  }
  const payload = {
    priceId,
    ...(creditsPriceId ? { creditsPriceId } : {}),
    timestamp: Date.now(),
    nonce: Math.random().toString(36).slice(2, 11),
  }
  const json = JSON.stringify(payload)
  // btoa is native in browsers + Node 16+. The pricing-components iframe
  // uses atob() to decode, which expects ASCII — JSON of priceIds always is.
  return typeof btoa === 'function'
    ? btoa(json)
    : Buffer.from(json, 'utf-8').toString('base64')
}

/**
 * Pull the Growth Plan record out of a site-profile/v2 pricing payload.
 * Returns null if not found — caller should fall back to PRICE_FALLBACKS.
 *
 * @param {object} pricing  data.pricing block from site-profile/v2
 */
export function findRecurringPlan(pricing, planName) {
  if (!pricing) return null
  const list = pricing['Recurring Plans']
  if (!Array.isArray(list)) return null
  return list.find((p) => p?.['Plan name'] === planName) || null
}

/**
 * Pull a Lifetime plan record out of a site-profile/v2 pricing payload.
 *
 * @param {object} pricing  data.pricing block from site-profile/v2
 */
export function findLifetimePlan(pricing, planName) {
  if (!pricing) return null
  const list = pricing['Lifetime Deals Plans']
  if (!Array.isArray(list)) return null
  return list.find((p) => p?.['Plan name'] === planName) || null
}

/**
 * site-profile/v2 stores prices as display strings like "$29/mo." — pull
 * the integer out. Returns the fallback if the string can't be parsed.
 */
export function parsePriceDisplay(displayString, fallback) {
  if (typeof displayString !== 'string') return fallback
  const match = displayString.match(/\d+(?:\.\d+)?/)
  if (!match) return fallback
  const n = Number(match[0])
  return Number.isFinite(n) ? n : fallback
}
